import {
  GROUP_TYPE_TO_PART_TYPE,
  MaterialGroup,
} from '@cutr/constants/cutlist';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useHasAiSuggestions } from '@/api/aiSuggestions';
import {
  useActiveGroup,
  useActiveGroupGrainDirection,
  useActiveGroupParts,
  useActiveGroupType,
  useMaterialGroupState,
} from '@/api/materialsGroup';
import { useMixedNestingStore } from '@/api/mixedNesting';
import { useActions, useCutlistState } from '@/api/store';
import { EdgesTooltip } from '@/blocks/EdgesTooltip';
import { InfoMessage } from '@/blocks/InfoMessage';
import { StarterTierNesting } from '@/interfaces/StarterTierNesting';
import { Icon, Info } from '@/primitives/Icons';
import { isStarterTier, useCurrentFeatures } from '@/theme';
import { useNextInputOnEnter } from '@/utils/hooks';

import { AgentNesting } from '../AgentNesting';
import { ActiveGroupPartErrors, Errors } from '../Errors';
import { EmptyTableNudge } from '../PartTableCommon/EmptyTableNudge';
import { MaterialGroupActions } from '../PartTableCommon/MaterialGroupActions';
import styles from '../PartTableCommon/styles.module.css';
import { useTableErrors } from '../PartTableCommon/useTableErrors';
import { EditEdgebanding } from './EdgebandingModal';
import { EditEdgeGrooves } from './EdgeGroovesModal';
import { EditEdgeProfiling } from './EdgeProfilingModal';
import { MaterialGroupHeader } from './MaterialGroupHeader';
import {
  SelectedPartsProvider,
  useSelectedPartsContext,
} from './SelectedPartsContext';
import { TableRow } from './TableRow';

export const AgentQuotingPartTable = () => {
  return (
    <SelectedPartsProvider>
      <div className={styles.orderSection}>
        <MaterialGroupSteps />
      </div>
    </SelectedPartsProvider>
  );
};

const MaterialGroupSteps = () => {
  const groups = useMaterialGroupState((state) => state.groups);
  const activeTab = useMaterialGroupState((state) => state.activeGroup);
  const activeGroup = useActiveGroup();
  const { t } = useTranslation();
  const starter = isStarterTier();
  const nestedGroups = useMixedNestingStore((state) =>
    state.groups.filter((group) => group.id === activeGroup?.id)
  );
  return (
    <>
      <form name="parts">
        <fieldset
          style={{ border: 0, padding: 0, margin: 0, minInlineSize: 'auto' }}
        >
          {groups
            .filter((g) => g.id === activeTab)
            .map((g) => (
              <MaterialGroupComponent key={g.id} group={g} />
            ))}
        </fieldset>
      </form>

      {/* 3. Nesting */}
      {!starter && activeGroup?.type && activeGroup.type !== 'beams' && (
        <>
          <h3>{t('agent.quoteFlow.nesting')}</h3>
          {nestedGroups.length > 0 ? (
            <StarterTierNesting groups={nestedGroups} />
          ) : (
            <AgentNesting />
          )}
        </>
      )}
    </>
  );
};

const MaterialGroupComponent = ({ group }: { group: MaterialGroup }) => {
  const activeGroup = useActiveGroup();
  const { t } = useTranslation();
  const hasLlmSuggestions = useHasAiSuggestions();

  return (
    <section
      key={group.id}
      className={styles.partSection}
      id={`section-${group.id}`}
    >
      <div>
        {hasLlmSuggestions && (
          <InfoMessage
            message={t('agent.quoteFlow.llmFlow.orderLevelNotice')}
            variant="ai"
          />
        )}
        {/* 1. Choose a material */}
        <MaterialGroupHeader group={group} />
      </div>

      {/* 2. Configure parts to cut */}
      {activeGroup?.type && <Table group={group} />}
    </section>
  );
};

const Table = ({ group }: { group: MaterialGroup }) => {
  const { t } = useTranslation();

  useTableErrors();

  return (
    <>
      <h3>{t('agent.quoteFlow.partsToCut')}</h3>
      <ActiveGroupPartErrors />
      <table>
        <Head />
        <Body group={group} />
      </table>

      <EmptyTableNudge group={group} />
      <Actions group={group} />
      <Errors />
    </>
  );
};

const Actions = ({ group }: { group: MaterialGroup }) => {
  const { edgebandingUI, edgeGroovingUI, hasEdgeProfiling } =
    useCurrentFeatures();
  const isBeamGroup = group.type === 'beams';
  return (
    <div className="flexAlign">
      <MaterialGroupActions group={group} />

      {!isBeamGroup && edgebandingUI && <EditEdgebanding />}

      {!isBeamGroup && edgeGroovingUI && <EditEdgeGrooves />}

      {!isBeamGroup && hasEdgeProfiling && <EditEdgeProfiling />}
    </div>
  );
};

type TableHeader = {
  labelKey: string;
  id?: string;
  width?: string;
  colSpan?: number;
  tooltipKey?: string;
};

const tableHeaders: TableHeader[] = [
  { labelKey: 'field.partName.label', id: 'label', width: '80px' },
  { labelKey: 'field.quantity.label' },
  { labelKey: 'field.length.label' },
  { labelKey: 'field.width.label' },
  { labelKey: 'field.cncMinutes.label', id: 'cncMinutes' },
  { labelKey: 'field.edgeband.label', id: 'edgebanding', colSpan: 5 },
  { labelKey: 'field.edgeGrooving.label', id: 'edgeGrooving', colSpan: 5 },
  { labelKey: 'field.edgeProfile.label', id: 'edgeProfiling', colSpan: 5 },
  { labelKey: 'field.grainDirection.label', id: 'grain' },
  { labelKey: 'field.aiSuggestions.label', id: 'aiSuggestions' },
  { labelKey: 'field.labeling', id: 'createLabel' },
  { labelKey: '', width: '50px' },
];

const beamTableHeaders: TableHeader[] = [
  { labelKey: 'field.partName.label', id: 'label', width: '80px' },
  { labelKey: 'field.quantity.label' },
  { labelKey: 'field.profileThickness.label' },
  { labelKey: 'field.profileWidth.label' },
  { labelKey: 'field.length.label' },
  {
    labelKey: 'field.roundedEdge.label',
    tooltipKey: 'field.roundedEdge.tooltip',
  },
  { labelKey: '', width: '50px' },
];

const useHeaders = () => {
  const activeGroupType = useActiveGroupType();
  const hasGrainDirection = useActiveGroupGrainDirection();
  const {
    edgebandingUI,
    edgeGroovingUI,
    agentCncMinutes,
    hasEdgeProfiling,
    hasCreateLabels,
  } = useCurrentFeatures();

  if (activeGroupType === 'beams') {
    return beamTableHeaders;
  }

  return tableHeaders.filter(({ id }) => {
    if (id === 'grain')
      return hasGrainDirection && activeGroupType !== 'sheets-only';
    if (id === 'edgebanding')
      return edgebandingUI && activeGroupType !== 'sheets-only';
    if (id === 'edgeGrooving')
      return edgeGroovingUI && activeGroupType !== 'sheets-only';
    if (id === 'edgeProfiling')
      return hasEdgeProfiling && activeGroupType !== 'sheets-only';
    if (id === 'cncMinutes')
      return agentCncMinutes && activeGroupType !== 'sheets-only';
    if (id === 'aiSuggestions') return activeGroupType !== 'sheets-only';
    if (id === 'createLabel')
      return hasCreateLabels === 'part' && activeGroupType !== 'sheets-only';
    return true;
  });
};

const Head = () => {
  const { t } = useTranslation();
  const hasGrainDirection = useActiveGroupGrainDirection();
  const headers = useHeaders();
  const { selectedParts, disableAll, enableAll } = useSelectedPartsContext();
  const {
    edgebandingUI,
    edgeGroovingUI,
    agentCncMinutes,
    hasEdgeProfiling,
    hasCreateLabels,
  } = useCurrentFeatures();

  const activeGroupType = useActiveGroupType();
  const activeGroupId = useMaterialGroupState((state) => state.activeGroup);
  const activeGroupPartsCount = useCutlistState(
    (state) => state.parts.filter((p) => p.groupId === activeGroupId).length
  );

  const handleMasterCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      enableAll();
    } else {
      disableAll();
    }
  };

  const headerText = (key?: string) => (key ? t(`cutlist-form.${key}`) : '');
  return (
    <thead>
      <tr>
        {activeGroupType !== 'sheets-only' && activeGroupType !== 'beams' && (
          <th style={{ height: '1px' }}>
            <label htmlFor="masterCheckbox" className={styles.label}>
              <input
                id="masterCheckbox"
                type="checkbox"
                onChange={handleMasterCheckbox}
                checked={selectedParts.length === activeGroupPartsCount}
              />
              <span className="visually-hidden">
                {selectedParts.length === activeGroupPartsCount
                  ? t('cutlist-form.deselectAllParts')
                  : t('cutlist-form.selectAllParts')}
              </span>
            </label>
          </th>
        )}
        {headers.map((h, i) => (
          <th
            key={h.labelKey || i}
            style={{ minWidth: h.width }}
            colSpan={h.colSpan ?? 1}
          >
            {h.id === 'edgebanding' ||
            h.id === 'edgeGrooving' ||
            h.id === 'edgeProfiling' ? (
              <div>
                {headerText(h.labelKey)}
                <EdgesTooltip />
              </div>
            ) : (
              <div>
                {headerText(h.labelKey)}
                {h.tooltipKey && (
                  <Icon
                    icon={<Info />}
                    color="var(--gray-7)"
                    className="tooltip"
                    data-tooltip={t(`cutlist-form.${h.tooltipKey}`)}
                  />
                )}
              </div>
            )}
          </th>
        ))}
      </tr>
      {activeGroupType !== 'sheets-only' && activeGroupType !== 'beams' && (
        <tr>
          <th />
          <th />
          <th />
          <th />
          <th />
          {agentCncMinutes && <th />}
          {edgebandingUI && (
            <>
              <th>{t('cutlist-form.field.edgebanding.sides.l1')}</th>
              <th>{t('cutlist-form.field.edgebanding.sides.l2')}</th>
              <th>{t('cutlist-form.field.edgebanding.sides.w1')}</th>
              <th>{t('cutlist-form.field.edgebanding.sides.w2')}</th>
              <th />
            </>
          )}
          {edgeGroovingUI && (
            <>
              <th>{t('cutlist-form.field.edgebanding.sides.l1')}</th>
              <th>{t('cutlist-form.field.edgebanding.sides.l2')}</th>
              <th>{t('cutlist-form.field.edgebanding.sides.w1')}</th>
              <th>{t('cutlist-form.field.edgebanding.sides.w2')}</th>
              <th />
            </>
          )}
          {hasEdgeProfiling && (
            <>
              <th>{t('cutlist-form.field.edgebanding.sides.l1')}</th>
              <th>{t('cutlist-form.field.edgebanding.sides.l2')}</th>
              <th>{t('cutlist-form.field.edgebanding.sides.w1')}</th>
              <th>{t('cutlist-form.field.edgebanding.sides.w2')}</th>
              <th />
            </>
          )}
          {hasGrainDirection && <th />}
          {<th />}
          {hasCreateLabels === 'part' && <th />}
          <th />
        </tr>
      )}
    </thead>
  );
};

const Body = ({ group }: { group: MaterialGroup }) => {
  const ref = React.useRef(null);
  const shouldFocusLast = React.useRef(false);
  const { addPart } = useActions();
  const parts = useActiveGroupParts();
  useNextInputOnEnter();

  React.useEffect(() => {
    if (!ref.current) return;
    const element = ref.current as HTMLElement;

    const onFocusOut = (e: FocusEvent) => {
      if (element.contains(e.relatedTarget as HTMLElement)) return;
      const time = e.timeStamp;
      window.addEventListener(
        'keyup',
        (e) => {
          if (e.key === 'Tab' && e.timeStamp - time < 200) {
            const partType =
              GROUP_TYPE_TO_PART_TYPE[group.type || 'panels-and-strips'];
            addPart(group, partType);
            shouldFocusLast.current = true;
          }
        },
        { once: true }
      );
    };
    element.addEventListener('focusout', onFocusOut);
    return () => {
      element.removeEventListener('focusout', onFocusOut);
    };
  }, [ref, addPart, group]);

  React.useEffect(() => {
    if (!ref.current) return;
    if (!shouldFocusLast.current) return;

    const element = ref.current as HTMLElement;
    const lastInput = element.querySelector(
      'tr:last-child input:first-of-type'
    ) as HTMLInputElement;
    lastInput?.focus();
    shouldFocusLast.current = false;
  }, [parts, ref, shouldFocusLast]);

  return (
    <tbody ref={ref}>
      {parts.map((part) => {
        return <TableRow part={part} key={part.id} />;
      })}
    </tbody>
  );
};
